import React, { useEffect } from 'react';
import { ScrollView, StyleSheet, View, RefreshControl } from 'react-native';
import { Screen, StackScreenProps } from '@rahtab/stack-navigator';
import { Button, Card, CardCaption, CardContainer, getSpacing, ImageButton, Text } from '@rahtab/ui';
import ChevronLeftIcon from '@rahtab/ui/dist/icons/ChevronLeftIcon';
import CalenderIcon from '@rahtab/ui/dist/icons/CalenderIcon';
// import ReturnLoadIcon from '@rahtab/ui/dist/icons/ReturnLoadIcon';
import StockIcon from '@rahtab/ui/dist/icons/StockIcon';
import InventoryEditIcon from '@rahtab/ui/dist/icons/InventoryEditIcon';
import CalendarIcon from '@rahtab/ui/dist/icons/CalenderIcon';
import { RootStackType } from '../../Router';
import LoadPieChart from '@rahtab/system/lib/components/Charts/LoadPieChart';
import { useLoad } from 'hooks/InventoryHooks/useLoad';
import { fixDateTime, withSeparator, withToman } from '@rahtab/system/lib/tools/string';
import LoadTag from '../../components/LoadTag';
import { useAppDispatch, useSelector } from '../../rahtab-redux';
import { loadsLoading as setLoadsLoading } from '../../rahtab-redux/slices/loadsSlice';

const ReturnLoadIcon = require('../../../assets/images/loadReturn.png');

const ReceivedLoad: React.VFC<ReceivedLoadProps> = ({ route }) => {
  const { assignFinalCode, merchandiseId } = route.params;
  const { called, data, loading, refetch } = useLoad({ assignFinalCode });
  const { loading: loadsLoading } = useSelector((state) => state.loads);
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (loadsLoading === 'refreshing') {
      refetch();
      dispatch(setLoadsLoading('idle'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadsLoading]);

  const isDelivered = data?.statusCode === '2';
  const isAssigned = data?.statusCode === '1';
  return (
    <Screen
      title="اطلاعات بار دریافتی"
      loading={!called && loading}
      data={data}
      renderMain={(load) => {
        return (
          <ScrollView
            refreshControl={<RefreshControl onRefresh={refetch} refreshing={called && loading} />}
            keyboardShouldPersistTaps="handled">
            <Card style={styles.card}>
              <CardContainer>
                <Text variant="title" color="primary" style={styles.justifiedText}>
                  {load.loadName}
                </Text>
                <View style={styles.idStatusView}>
                  <Text margin="none" color="description">
                    شناسه بار: <Text>{load.afIdentification}</Text>
                  </Text>
                  <View style={styles.deliveryTimeView}>
                    {!!load.regulationTag && <LoadTag status={load.regulationTag} statusCode={'controlled'} />}
                    <LoadTag status={load.status} statusCode={load.statusCode} />
                  </View>
                </View>
                <Text color="description">
                  نام کالا: <Text>{load.templateName}</Text>
                </Text>
                <Text color="description" style={styles.justifiedText}>
                  تخصیص دهنده: <Text>{load.middlemanName}</Text>
                </Text>
                <Text color="description">
                  مقدار بار تخصیص شده:{' '}
                  <Text>
                    {withSeparator(load.initialAmount)} {load.measureName}
                  </Text>
                </Text>
                <Text color="description">
                  قیمت واحد: <Text>{withToman(load.initialPricePerUnit)}</Text>
                </Text>
                <Text color="description">
                  جمع کل: <Text>{withToman(load.totalPrice)}</Text>
                </Text>
                <View
                  style={StyleSheet.flatten([
                    styles.deliveryTimeView,
                    { marginBottom: isAssigned ? 0 : getSpacing() },
                  ])}>
                  <CalendarIcon style={StyleSheet.flatten([styles.calendarIcon])} />
                  <Text color="description" margin="none">
                    زمان تخصیص: <Text>{fixDateTime(load.assignDate)}</Text>
                  </Text>
                </View>
                {!isAssigned && (
                  <View style={styles.deliveryTimeView}>
                    <CalendarIcon style={styles.calendarIcon} />
                    <Text color="description" margin="none">
                      زمان اعلام تحویل: <Text>{fixDateTime(load.deliveryDate)}</Text>
                    </Text>
                  </View>
                )}
              </CardContainer>
            </Card>
            {!isDelivered && !isAssigned && (
              <>
                <Card style={StyleSheet.flatten([styles.card, { marginTop: 0, marginBottom: 0 }])}>
                  <CardCaption>
                    <Text margin="none" color="primary" variant="cardTitle" style={{ textAlign: 'center' }}>
                      وصول بار دریافتی
                    </Text>
                  </CardCaption>
                  <CardContainer>
                    <View style={styles.deliveryTimeView}>
                      <CalenderIcon style={styles.calendarIcon} />
                      <Text color="description">
                        زمان اعلام وصول بار دریافتی:
                        <Text> {fixDateTime(load.receiveDate)}</Text>
                      </Text>
                    </View>
                    <Text color="description">
                      مقدار بار وصول شده:
                      <Text>
                        {' '}
                        {withSeparator(load.receivedAmount)} {load.measureName}
                      </Text>
                    </Text>
                    <Text margin="none" color="description">
                      قیمت واحد دریافتی:
                      <Text margin="none"> {withToman(load.pricePerUnit)}</Text>
                    </Text>
                  </CardContainer>
                </Card>
                <LoadPieChart
                  correctionAmount={load.correctionAmount}
                  correctionPercent={load.correctionPercent}
                  measureName={load.measureName}
                  remainedAmount={load.remainingValue}
                  remainedPercent={load.remainingPercent}
                  returnedAmount={load.returnAmount}
                  returnedPercent={load.returnPercent}
                  saledAmount={load.saleAmount}
                  saledPercent={load.salePercent}
                />
                <View style={StyleSheet.flatten([styles.rowView, styles.buttons])}>
                  <ImageButton image={ReturnLoadIcon} name="بازگشت بار" disabled />
                  <ImageButton
                    svg={<InventoryEditIcon />}
                    name="اصلاح موجودی"
                    screenName={'FixLoadAmount'}
                    params={{ assignFinalCode }}
                  />
                  <ImageButton
                    svg={<StockIcon />}
                    name="عرضه بار"
                    screenName={'StockList'}
                    params={{ assignFinalCode }}
                  />
                </View>
              </>
            )}
            {isDelivered && (
              <Button
                screenName="AnounceLoadDelivery"
                params={{
                  assignFinalCode,
                  merchandiseId,
                }}
                style={styles.fixAmountButton}
                text="اعلام وصول بار تخصیص یافته"
                color="primary">
                <ChevronLeftIcon />
              </Button>
            )}
          </ScrollView>
        );
      }}
    />
  );
};
export default ReceivedLoad;

const styles = StyleSheet.create({
  card: {
    margin: getSpacing(),
  },
  deliveryTimeView: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  calendarIcon: {
    marginRight: getSpacing(),
  },
  idStatusView: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: getSpacing(0.5),
  },
  fixAmountButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginHorizontal: getSpacing(),
    marginBottom: getSpacing(),
    paddingHorizontal: getSpacing(),
  },
  justifiedText: {
    textAlign: 'justify',
  },
  rowLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: getSpacing(),
  },
  rowView: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  chart: {
    justifyContent: 'center',
    marginTop: 0,
  },
  dotIcon: {
    marginRight: getSpacing(),
    marginTop: 3,
  },
  buttons: {
    marginHorizontal: getSpacing(0.5),
    marginBottom: getSpacing(0.5),
  },
});

type ReceivedLoadProps = StackScreenProps<RootStackType, 'ReceivedLoad'>;
